"use client";

import React from "react";
import type { CSSProperties } from "react";
import * as Icon from "@phosphor-icons/react/dist/ssr";

type BannerCategory = {
  id: number;
  name: string;
  /** If set, used for `/shop?category=` instead of `name.toUpperCase()` */
  categoryQuery?: string;
  subCategory: { id: number; name: string }[];
  img: string;
};

const shopCategoryHref = (cat: BannerCategory) =>
  `/shop?category=${encodeURIComponent(cat.categoryQuery ?? cat.name.toUpperCase())}`;

const Banner = () => {
  const categoryList: BannerCategory[] = [
    {
      id: 0,
      name: "Pyjamas & Gandora",
      subCategory: [
        {
          id: 0,
          name: "Pyjamas élégantes",
        },
        {
          id: 1,
          name: "Gandoras traditionnels",
        },
        {
          id: 2,
          name: "Ensembles de nuits",
        },
      ],
      img: "/Pyjamas-Gandora.jpg",
    },
    {
      id: 1,
      name: "Chale & foulard",
      subCategory: [
        {
          id: 0,
          name: "Chales en soie",
        },
        {
          id: 1,
          name: "Foulards colorés",
        },
        {
          id: 2,
          name: "Écharpes d'hiver",
        },
      ],
      img: "/Chale-foulard.jpg",
    },
    {
      id: 2,
      name: "Lingerie",
      categoryQuery: "LINGERIE ET SOUS-VETEMENTS",
      subCategory: [
        {
          id: 0,
          name: "Lingerie fine",
        },
        {
          id: 1,
          name: "Sous-vêtements confortables",
        },
        {
          id: 2,
          name: "Ensembles assortis",
        },
      ],
      img: "/Lingerie-sous-vetements.jpg",
    },
    {
      id: 3,
      name: "Accessoires",
      subCategory: [
        {
          id: 0,
          name: "Élastiques décoratifs",
        },
        {
          id: 1,
          name: "Barrettes élégantes",
        },
        {
          id: 2,
          name: "Accessoires de coiffure",
        },
      ],
      img: "/Accessoires-cheveux.jpg",
    },
    {
      id: 4,
      name: "DIVERS",
      subCategory: [
        {
          id: 0,
          name: "Pantoufles & babouches",
        },
        {
          id: 1,
          name: "Serviettes de plage",
        },
        {
          id: 2,
          name: "Accessoires divers",
        },
      ],
      img: "/Accessoires-plage.jpg",
    },
    {
      id: 5,
      name: "Accessoires hammam",
      subCategory: [
        { id: 0, name: "Tassa" },
        { id: 1, name: "gant de savonnage" },
        { id: 2, name: "bonnet plastique" },
      ],
      img: "/accessoirehammam.jpg",
    },
  ];

  return (
    <>
      <section className="banner-block pb-10">
        <div className="w-[95%] m-auto">
          <div
            className="fx-reveal flex justify-between items-center pb-2 relative border-b border-line line-bottom mb-4"
          >
            <h3 className="text-orange font-bold">ACHETER PAR CATÉGORIES</h3>
          </div>


          <div className="hidden max-sm:block">
            {/* MOBILE - IMPROVED VERSION */}
            <div className="grid grid-cols-2 gap-3 mx-2">
            {categoryList.map((category, index) => (
                <article
                  key={index}
                  className="fx-reveal bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                  style={{ "--fx-delay": `${index * 100}ms` } as CSSProperties}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={category.img}
                      alt={category.name}
                      width={400}
                      height={200}
                      className="object-fill w-full h-32"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>
                  
                  <div className="p-3">
                    <h3 className="font-bold text-sm text-gray-800 mb-2 leading-tight">
                      {category.name}
                    </h3>
                    
                    <div className="mb-3">
                      {category.subCategory.slice(0, 2).map((subcate, subIndex) => (
                        <p
                          key={subcate.id || subIndex}
                          className="text-xs text-gray-600 leading-tight mb-1"
                        >
                          • {subcate.name}
                        </p>
                      ))}
                      {category.subCategory.length > 2 && (
                        <p className="text-xs text-gray-500 italic">
                          +{category.subCategory.length - 2} autres
                        </p>
                      )}
                    </div>

                    <a
                      href={shopCategoryHref(category)}
                      className="w-full bg-pink text-white text-xs font-medium px-3 py-2 rounded-lg transition-all duration-200 hover:bg-pink/90 flex items-center justify-center gap-1"
                    >
                      Voir plus
                      <Icon.ArrowRight size={12} />
                    </a>
                  </div>
                </article>
              ))}
            </div>
            
            {/* Mobile Category Navigation */}
            <div className="mt-6 px-2">
              <div className="bg-gradient-to-r from-pink to-pink/80 rounded-xl p-4 text-center">
                <h4 className="text-white font-bold text-sm mb-2">
                  Découvrez toutes nos catégories
                </h4>
                <a
                  href="/shop"
                  className="inline-block bg-white text-pink font-medium px-4 py-2 rounded-lg text-sm transition-all duration-200 hover:bg-gray-50"
                >
                  Parcourir la boutique
                </a>
              </div>
            </div>
          </div>

          <div
  className="
    flex flex-wrap justify-center gap-4 mx-2
    max-sm:hidden
    max-md:justify-start max-sm:justify-center max-md:gap-5
  "
>
  {categoryList.slice(0, 5).map((category, index) => (
    <article
      key={category.id || index}
      className="
        fx-reveal flex flex-col items-center justify-center
        w-[18.2%] max-md:w-[31.4%]
        max-sm:border max-sm:border-secondary2 max-sm:rounded-lg
        animation-img
      "
      style={{ "--fx-delay": `${index * 80}ms` } as CSSProperties}
      aria-label={`Category: ${category.name}`}
    >
      <div className="relative h-[179px] overflow-hidden rounded-md">
        <img
          src={category.img}
          alt={category.name}
          width={800}
          height={800}
          className="object-cover w-full h-full"
          loading="lazy"
          decoding="async"
        />
      </div>

      <div className="text-center py-2 pb-4">
        <h3 className="font-bold uppercase text-lg">{category.name}</h3>

        <div className="py-3">
          {category.subCategory.map((subcate, subIndex) => (
            <p key={subcate.id || subIndex} className="text-secondary text-sm">
              {subcate.name}
            </p>
          ))}
        </div>

        <a
          href={shopCategoryHref(category)}
          className="
            inline-block
            border border-orange px-4 py-2 rounded-full
            text-sm text-orange
            transition-colors duration-200
            hover:bg-orange hover:text-white
            focus:outline-none focus:ring-2 focus:ring-orange focus:ring-offset-2
            flex items-center justify-center gap-2
          "
          aria-label={`See more products in category ${category.name}`}
        >
          Plus de Categorie
          <Icon.ArrowRight size={16} />
        </a>
      </div>
    </article>
  ))}
</div>


        </div>
      </section>
    </>
  );
};

export default Banner;
